import type { ServiceRequestStatus } from '@prisma/client';

import type {
  AgentConversationStatus,
  AgentMessageRole,
  AgentMessageStatus,
} from '@commenda/types';

export enum FilingsAuditorVerdict {
  PASS = 'PASS',
  FAIL = 'FAIL',
  INSUFFICIENT_EVIDENCE = 'INSUFFICIENT_EVIDENCE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  NEEDS_HUMAN_REVIEW = 'NEEDS_HUMAN_REVIEW',
}

export enum FilingsAuditorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum FilingsAuditorFindingResolution {
  REFUTED = 'REFUTED',
  SYSTEM_ERROR = 'SYSTEM_ERROR',
}

export enum FilingsAuditorFindingReviewDecision {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum FilingsAuditorListSortField {
  LAST_UPDATED_AT = 'lastUpdatedAt',
  COMPLETED_STEPS = 'completedSteps',
}

export type FilingsAuditorListSortOrder = 'asc' | 'desc';

export interface IFilingsAuditorListSort {
  sortBy: FilingsAuditorListSortField;
  sortOrder: FilingsAuditorListSortOrder;
}

export enum FilingsAuditorRunStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface IFilingsAuditorCitationView {
  surface: string;
  fileId?: number;
  msgId?: string;
  fileName?: string;
  description?: string;
  quotedText: string;
}

export interface IFilingsAuditorFindingView {
  id: string;
  checkId: string;
  verdict: FilingsAuditorVerdict | null;
  resolution: FilingsAuditorFindingResolution | null;
  severity: FilingsAuditorSeverity;
  confidence: number | null;
  headline: string | null;
  message: string;
  impact: string | null;
  mergedCheckIds: string[];
  consolidatedFrom: IFilingsAuditorConsolidatedFindingView[];
  citations: IFilingsAuditorCitationView[];
  reviewDecision: FilingsAuditorFindingReviewDecision | null;
  reviewNote: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
}

export interface IFilingsAuditorConsolidatedFindingView {
  checkId: string;
  headline: string | null;
  message: string;
}

export interface IFilingsAuditorRuleView {
  checkId: string;
  requirement: string;
  authoritativeCitation: string | null;
  severity: FilingsAuditorSeverity;
}

export interface IFilingsAuditorResultView {
  id: string;
  serviceRequestId: string;
  filingCompositeId: string;
  filingDisplayName: string;
  status: FilingsAuditorRunStatus;
  verdict: FilingsAuditorVerdict | null;
  findingCount: number;
  systemErrorCount: number;
  needsHumanReviewCount: number;
  findings: IFilingsAuditorFindingView[];
  ruleCatalog: IFilingsAuditorRuleView[];
  createdAt: string;
}

export interface IFilingsAuditorLatestResultView {
  id: string;
  status: FilingsAuditorRunStatus;
  verdict: FilingsAuditorVerdict | null;
  agentConversationId: string | null;
  passRate: number | null;
  failureRate: number | null;
  pendingReviewCount: number;
  createdAt: string;
}

export interface IFilingsAuditableServiceRequestView {
  serviceRequestId: string;
  entityName: string | null;
  companyName: string | null;
  filingCompositeId: string;
  filingDisplayName: string;
  serviceRequestStatus: ServiceRequestStatus;
  completedStepCount: number;
  totalStepCount: number;
  srUpdatedAt: string;
  latestResult: IFilingsAuditorLatestResultView | null;
}

export interface IFilingsAuditorMessageView {
  id: string;
  role: AgentMessageRole;
  content: string | null;
  status: AgentMessageStatus;
  createdAt: string;
}

export interface IFilingsAuditorConversationView {
  id: string;
  status: AgentConversationStatus;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messages: IFilingsAuditorMessageView[];
}

export interface IFilingsAuditorRunView {
  conversation: IFilingsAuditorConversationView;
  result: IFilingsAuditorResultView | null;
}

// ─── Endpoint response envelopes + mutation result ───────────────────────────

export interface IFilingsAuditableServiceRequestsResponse {
  serviceRequests: IFilingsAuditableServiceRequestView[];
  nextCursor: string | null;
  count: number;
}

export interface IFilingsAuditorRunResponse {
  run: IFilingsAuditorRunView;
}

export interface IFilingsAuditorSupportedFilingsResponse {
  filings: string[];
  count: number;
}

export interface ISubmitFilingsAuditorRunData {
  conversationId: string;
}

export interface IFilingsAuditorBulkRunResultView {
  serviceRequestId: string;
  conversationId: string | null;
  success: boolean;
  error: string | null;
}

export interface IFilingsAuditorBulkRunResponse {
  results: IFilingsAuditorBulkRunResultView[];
  count: number;
}

export type IFilingsAuditorMutationResult<TData = undefined> =
  | { success: true; data: TData; error?: undefined }
  | { success: false; data?: undefined; error: string };

export interface IAuditableServiceRequestsQuery {
  q?: string;
  filingCompositeId?: string;
  cursor?: string;
  limit?: number;
  sortBy?: FilingsAuditorListSortField;
  sortOrder?: FilingsAuditorListSortOrder;
}

export interface IAuditableServiceRequestsOptions {
  refreshIntervalMs: number;
}
